import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getCountry, COUNTRIES } from "@/data/countries";
import { useSeo } from "@/hooks/useSeo";
import { Reveal } from "@/components/common/Reveal";
import { CTASection } from "@/components/home/CTASection";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Boat, Airplane, Lightning, CaretRight, CheckCircle, FileText, Package, ArrowRight, Globe } from "@phosphor-icons/react";
import { useT } from "@/context/LanguageContext";

export default function CountryLanding() {
  const { country: slug } = useParams();
  const data = getCountry(slug);
  const { t } = useT();

  const faqJsonLd = React.useMemo(
    () =>
      data
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: data.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null,
    [data]
  );

  const otherCountries = React.useMemo(() => COUNTRIES.filter((c) => c.slug !== slug), [slug]);

  useSeo({ title: data?.seoTitle, description: data?.seoDescription, image: data?.image, jsonLd: faqJsonLd });

  if (!data) return <Navigate to="/" replace />;

  const transitItems = [
    { icon: Boat, label: t("countryPage.seaFreight"), value: data.transit.sea },
    { icon: Airplane, label: t("countryPage.airFreight"), value: data.transit.air },
    { icon: Lightning, label: t("countryPage.express"), value: data.transit.express },
  ];

  return (
    <div data-testid={`country-page-${slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden bg-ink pt-32 pb-16 lg:pt-40 lg:pb-20" data-testid="country-hero">
        <img src={data.image} alt={`Import from China to ${data.country}`} className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/60" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <nav className="mb-5 flex items-center gap-1 text-xs text-slate-400">
            <Link to="/" className="hover:text-teal">{t("countryPage.home")}</Link>
            <CaretRight size={12} /> <span className="text-slate-300">{t("countryPage.destinations")}</span>
            <CaretRight size={12} /> <span className="text-slate-300">{data.country}</span>
          </nav>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-teal-light">
            <span className="mr-2 text-lg align-middle">{data.flag}</span> China → {data.country}
          </p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t("countryPage.importTitlePre")} {data.country}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">{data.intro}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact?type=quote" data-testid="country-cta-quote" className="rounded-md bg-gradient-to-r from-[#1E73BE] to-[#16B894] px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5">
              {t("countryPage.getQuoteTo")} {data.city}
            </Link>
            <Link to="/contact?type=consultation" className="rounded-md border border-white/30 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10">
              {t("countryPage.bookConsult")}
            </Link>
          </div>
        </div>
      </section>

      {/* Transit times */}
      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-teal">{t("countryPage.shippingTo")} {data.port}</p>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("countryPage.transitTitle")}</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {transitItems.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.08}>
                <div className="rounded-xl border border-slate-200 bg-mist p-7" data-testid={`country-transit-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white"><item.icon size={24} weight="duotone" /></div>
                  <p className="mt-5 text-sm font-medium text-slate-500">{item.label}</p>
                  <p className="font-heading text-2xl font-bold text-ink">{item.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 flex items-start gap-2 text-sm text-slate-500"><FileText size={18} className="mt-0.5 shrink-0 text-teal" /> {data.duties}</p>
        </div>
      </section>

      {/* Popular categories */}
      <section className="bg-mist py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("countryPage.popularImports")} {data.country}</h2>
          <div className="mt-8 flex flex-wrap gap-3">
            {data.popularCategories.map((c) => (
              <Link key={c} to="/contact?type=quote" className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:border-teal" data-testid="country-category">
                <Package size={16} weight="duotone" className="text-teal" /> {c}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-teal">{t("countryPage.customsOverline")}</p>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("countryPage.importingToPre")} {data.country}{t("countryPage.importingToPost")}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {data.compliance.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="h-full rounded-xl border border-slate-200 bg-white p-7" data-testid={`country-compliance-${i}`}>
                  <CheckCircle size={26} weight="fill" className="text-teal" />
                  <h3 className="mt-4 font-heading text-lg font-semibold text-ink">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-ink py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <p className="font-heading text-2xl font-medium leading-relaxed text-white sm:text-3xl">"{data.testimonial.quote}"</p>
          <p className="mt-6 text-sm font-semibold text-teal-light">{data.testimonial.name}</p>
          <p className="text-xs text-slate-400">{data.testimonial.role}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-ink sm:text-4xl">China → {data.country} {t("countryPage.faq")}</h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="country-faq">
            {data.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-heading text-base font-semibold text-ink">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Other destinations */}
      <section className="bg-mist py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex items-center gap-2"><Globe size={22} weight="duotone" className="text-teal" /><h2 className="font-heading text-xl font-semibold text-ink">{t("countryPage.otherDestinations")}</h2></div>
          <div className="mt-6 flex flex-wrap gap-3">
            {otherCountries.map((c) => (
              <Link key={c.slug} to={`/import/${c.slug}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-ink transition-all hover:-translate-y-0.5 hover:border-teal" data-testid={`country-link-${c.slug}`}>
                <span>{c.flag}</span> China → {c.country} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
